import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useAuth } from '@src/hooks/auth/useAuth';
import { useJobFinishedSubscription } from '@src/modules/hierarchy/hierarchyQuery.generated';
import { downloadFile } from '@src/utils/hierarchies/downloadFile';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import JobSubscriptionContext from './JobSubscriptionContext';

const JobSubscriptionContextProvider = ({ children }: { children: ReactNode }) => {
    const intl = useIntl();
    const { sessionData } = useAuth();

    // activeJobIds: jobs that have been registered and are still pending
    const [activeJobIds, setActiveJobIds] = useState<Set<string>>(new Set());
    // finishedJobIds: jobs that completed; consumers read this to detect their job finishing
    const [finishedJobIds, setFinishedJobIds] = useState<Set<string>>(new Set());

    const addJobId = useCallback((id: string) => {
        setActiveJobIds(prev => new Set([...prev, id]));
    }, []);

    const clearFinishedJob = useCallback((id: string) => {
        setFinishedJobIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    // When tracking a single job, pass its jobId so the server can check if it
    // already completed before the subscription started (catch-up mechanism).
    const activeJobArray = Array.from(activeJobIds);
    const singleJobId = activeJobArray.length === 1 ? activeJobArray[0] : undefined;

    // Skip the subscription when there are no active jobs to avoid unnecessary traffic
    const { data } = useJobFinishedSubscription({
        shouldResubscribe: true,
        variables: {
            company: sessionData?.attributes?.company,
            jobId: singleJobId,
        },
        skip: activeJobIds.size === 0,
    });

    useEffect(() => {
        if (!data?.jobFinished) return;

        const { jobId, filename, type } = data.jobFinished;

        // Ignore events for jobs we are not tracking
        if (!jobId || !activeJobIds.has(jobId)) return;

        SnackBarUtils.success(
            intl.formatMessage({ id: 'job-finished', defaultMessage: 'Job finished' }),
            { persist: true },
        );

        const isExport = filename && type;
        if (isExport) downloadFile(filename, type);

        // Move the job from active to finished so consumers can react
        setActiveJobIds(prev => {
            const next = new Set(prev);
            next.delete(jobId);
            return next;
        });
        setFinishedJobIds(prev => new Set([...prev, jobId]));
    }, [data]);

    const contextValue = useMemo(
        () => ({ addJobId, finishedJobIds, clearFinishedJob }),
        [addJobId, finishedJobIds, clearFinishedJob],
    );

    return (
        <JobSubscriptionContext.Provider value={contextValue}>
            {children}
        </JobSubscriptionContext.Provider>
    );
};

export default JobSubscriptionContextProvider;
